import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, AREA_UNITS } from "@/lib/constants";
import { submitProperty } from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

export default function SubmitProperty() {
  const [f, setF] = useState({
    title: "", description: "", category: "residential", purpose: "sell",
    city: "Indore", state: "Madhya Pradesh", location: "",
    price: "", price_label: "", area: "", area_unit: "sqft", bhk: "",
    owner_name: "", owner_phone: "", owner_email: "",
    images: [],
  });
  const [imageInput, setImageInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setF({ ...f, [k]: v });

  const addImage = () => {
    if (!imageInput) return;
    setF({ ...f, images: [...f.images, imageInput] });
    setImageInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.owner_name || !f.owner_phone) return toast.error("Please fill required fields");
    setSubmitting(true);
    try {
      await submitProperty({
        ...f,
        price: Number(f.price) || 0,
        area: Number(f.area) || 0,
        bhk: f.bhk ? Number(f.bhk) : undefined,
        source: "public",
      });
      toast.success("Property submitted! We'll verify and contact you within 24 hours.");
      setF({ title: "", description: "", category: "residential", purpose: "sell", city: "Indore", state: "Madhya Pradesh", location: "", price: "", price_label: "", area: "", area_unit: "sqft", bhk: "", owner_name: "", owner_phone: "", owner_email: "", images: [] });
    } catch { toast.error("Submission failed. Try again."); }
    setSubmitting(false);
  };

  return (
    <div className="pt-28 pb-24 bg-brand-ivory">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <div className="text-center mb-12">
            <div className="divider-gold">Owner Portal</div>
            <h1 className="mt-4 font-serif text-5xl lg:text-6xl text-brand-navy">List Your Property</h1>
            <p className="mt-3 text-brand-charcoal/70">Reach serious buyers across India. Our team will verify and publish within 24 hours.</p>
          </div>
        </Reveal>

        <Reveal>
          <form onSubmit={submit} className="bg-white border border-brand-gold/20 p-8 space-y-6">
            <Section title="Property Details">
              <Field label="Property Title*"><Input required data-testid="sp-title" value={f.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g., 3BHK Apartment in Vijay Nagar" /></Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Category*">
                  <Select value={f.category} onValueChange={(v) => update("category", v)}>
                    <SelectTrigger data-testid="sp-category" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Purpose*">
                  <Select value={f.purpose} onValueChange={(v) => update("purpose", v)}>
                    <SelectTrigger data-testid="sp-purpose" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sell">Sell</SelectItem>
                      <SelectItem value="rent">Rent</SelectItem>
                      <SelectItem value="lease">Lease</SelectItem>
                      <SelectItem value="jv">Joint Venture</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Description"><Textarea rows={4} data-testid="sp-desc" value={f.description} onChange={(e) => update("description", e.target.value)} /></Field>
            </Section>

            <Section title="Location">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="City*"><Input required data-testid="sp-city" value={f.city} onChange={(e) => update("city", e.target.value)} /></Field>
                <Field label="State*"><Input required data-testid="sp-state" value={f.state} onChange={(e) => update("state", e.target.value)} /></Field>
                <Field label="Locality"><Input data-testid="sp-loc" value={f.location} onChange={(e) => update("location", e.target.value)} /></Field>
              </div>
            </Section>

            <Section title="Pricing & Size">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Price (₹)"><Input type="number" data-testid="sp-price" value={f.price} onChange={(e) => update("price", e.target.value)} /></Field>
                <Field label="Price Label"><Input placeholder="e.g., ₹ 85 Lakh" data-testid="sp-price-label" value={f.price_label} onChange={(e) => update("price_label", e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Area"><Input type="number" data-testid="sp-area" value={f.area} onChange={(e) => update("area", e.target.value)} /></Field>
                <Field label="Unit">
                  <Select value={f.area_unit} onValueChange={(v) => update("area_unit", v)}>
                    <SelectTrigger data-testid="sp-unit" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(AREA_UNITS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="BHK (if applicable)"><Input type="number" data-testid="sp-bhk" value={f.bhk} onChange={(e) => update("bhk", e.target.value)} /></Field>
              </div>
            </Section>

            <Section title="Images">
              <div className="flex gap-2">
                <Input placeholder="Paste image URL and click Add" data-testid="sp-img-input" value={imageInput} onChange={(e) => setImageInput(e.target.value)} className="rounded-sm" />
                <Button type="button" onClick={addImage} data-testid="sp-img-add" className="rounded-sm bg-brand-gold text-brand-navy hover:opacity-90">Add</Button>
              </div>
              {f.images.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-3">
                  {f.images.map((img, i) => (
                    <div key={i} className="relative w-20 h-20 border">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setF({ ...f, images: f.images.filter((_, j) => j !== i) })} className="absolute -top-2 -right-2 bg-brand-navy text-white rounded-full w-5 h-5 text-xs">×</button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Owner Details">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Full Name*"><Input required data-testid="sp-owner-name" value={f.owner_name} onChange={(e) => update("owner_name", e.target.value)} /></Field>
                <Field label="Phone*"><Input required data-testid="sp-owner-phone" value={f.owner_phone} onChange={(e) => update("owner_phone", e.target.value)} /></Field>
                <Field label="Email"><Input data-testid="sp-owner-email" value={f.owner_email} onChange={(e) => update("owner_email", e.target.value)} /></Field>
              </div>
            </Section>

            <div className="pt-4 border-t border-brand-gold/20 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-brand-charcoal/60">By submitting, you agree to be contacted by Wilshire Buildcon for verification and buyer connect.</p>
              <Button type="submit" disabled={submitting} data-testid="sp-submit" className="btn-gold rounded-sm h-12 px-8">{submitting ? "Submitting..." : "Submit Property"}</Button>
            </div>
          </form>
        </Reveal>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="text-[10px] tracking-widest uppercase text-brand-gold font-bold border-b border-brand-gold/30 pb-2">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
